/**
 * mock-receiver-modal.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Observable } from "rxjs/Rx";
import { AuthService } from "../auth/auth.service";
import { CompositeConnection, SYM_SKIP_SIPHON }
  from "../server/connection/composite-connection";
import { IncomingMessage, Message, Tagged, ValidatingClass }
  from "../server/messages/general/";
import { MessagingService } from "../server/messaging.service";
import { INCOMING_MESSAGE_MAP }
  from "../server/validation/incoming-message-map";
import { Identifiable } from "../support/types/aliases";
import { arrayRemove } from "../support/utilities/";

import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";

const DEFAULT_MESSAGE : string = "{\n\"tag\": 1\n}";

@Component({
  template: `
  <div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h4 class="modal-title">
      Mock receiver tool &emsp;
      <span class="badge">
        {{ready ? "connected" : "DISCONNECTED"}}
        &mdash;
        {{auth.authenticated ? "logged in" : "GUEST"}}
      </span>
    </h4>
  </div>

  <div class="modal-body">
    <form [formGroup]="form">
      <div>
        <label>Enter message JSON: (ID will automatically be assigned)</label>
        <button
          id="sendButton"
          class="btn btn-primary pull-right"
          (click)="tryReceive()"
          [disabled]="!ready">
          Parse &amp; "Receive"
        </button>
      </div>

      <textarea
        class="form-control"
        rows="8"
        formControlName="rawMessage">
      </textarea>
      <br>
    </form>
  </div>

  <div class="modal-body">
    <h4>
      Incoming Message Interceptor
      <button
        type="button"
        class="btn btn-sm"
        [class.btn-danger]="spigot.siphoning"
        [class.btn-success]="!spigot.siphoning"
        [disabled]="!spigot"
        (click)="spigot.toggle(); dialog.close()">
        {{spigot.siphoning ? "Stop" : "Start"}} Intercepting
      </button>
    </h4>

    <div
      *ngFor="let msg of spigot.siphoned"
      class="panel panel-default">
      <div class="panel-heading" (click)="panel.open = !panel.open">
        <h3 class="panel-title">
          <!--<span
            class="glyphicon"
            [class.glyphicon-expand]="!panel.open"
            [class.glyphicon-collapse-down]="panel.open">
          </span>-->
          <span
            class="badge"
            [class.success]="msg.id > 0">
            {{msg.id}}
          </span>
          {{nameForTag(msg.tag)}}
          <span class="btn-group pull-right">
            <button
              type="button"
              class="btn btn-xs btn-info"
              (click)="passThrough(msg)">
              Pass Through
            </button>
            <button
              type="button"
              class="btn btn-xs btn-default"
              (click)="modify(msg)">
              Modify
            </button>
          </span>
        </h3>
      </div>
      <div #panel class="panel-body collapse" [class.in]="panel.open">
        <prettyjson [obj]="msg"></prettyjson>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button
      type="button"
      class="btn btn-primary"
      (click)="dialog.close()">
      Close
    </button>
  </div>
  `,
  styles: [
    "/deep/ prettyjson pre {outline: none !important;}",
    "/deep/ #sendButton {position: relative; top: -8px;}",
    ".modal-body + .modal-body { border-top: 1px solid #ddd; }"
    /* TODO: globalize style ^^^ */
  ]
})
export class MockReceiverModalComponent
  implements ModalComponent<BSModalContext>, OnInit
{
  constructor (
    protected messaging : MessagingService,
    protected auth : AuthService,
    public dialog : DialogRef<BSModalContext>
  ) {/* DI */}

  public ngOnInit () : void
  {
    this.messaging.ready.subscribe((ready : boolean) => {
      this.ready = ready;
      this.spigot = this.messaging.connection as CompositeConnection<Message>;
    });
  }

  private mockIdAllocator : number = -10000;

  private rawMessage : FormControl = new FormControl(DEFAULT_MESSAGE);

  public spigot : CompositeConnection<Message>;

  /**
   * The form holding the dialog's controls
   * @type {FormGroup}
   */
  public form : FormGroup = new FormGroup({rawMessage: this.rawMessage});

  /**
   * Snapshot of the {@link MessagingService}'s readiness
   * @type {boolean}
   */
  public ready : boolean;

  /**
   * Attempt to parse the user's input and send it as a {@link Message}.
   */
  public tryReceive () : void
  {
    const message : string = this.rawMessage.value;
    try
    {
      const objectForm : any = JSON.parse(message);
      if (typeof objectForm !== "object")
      {
        throw new Error("Outermost level of message must be object!");
      }
      if (!this.isTagged(objectForm))
      {
        throw new Error("Message must have a (numeric) 'tag' property!");
      }
      // Make the object message-like and send it
      this.receive(objectForm);
    }
    catch (e)
    {
      // We threw an exception, almost certainly due to a parsing failure, so
      // alert the user (this is debug-only, we don't need no fancy stuff!) and
      // bail on the send attempt.
      console.error(e);
      alert("Could not parse your message content! See console for details.");
    }
  }

  private receive (message : any) : void
  {
    message[SYM_SKIP_SIPHON] = true;
    if (message.id === undefined)
    {
      message.id = this.mockIdAllocator--;
    }
    this.messaging.mockInput.receive(message);
  }

  public passThrough (toResume : Message) : void
  {
    arrayRemove(this.spigot.siphoned, toResume);
    this.receive(toResume);
  }

  public modify (toModify : Message) : void
  {
    arrayRemove(this.spigot.siphoned, toModify);
    this.rawMessage.setValue(JSON.stringify(toModify));
  }

  /**
   * Determine if the object has a valid tag
   * @param  {any} m a potential Tagged entity
   * @return {boolean}
   */
  public isTagged (m : any) : m is Tagged
  {
    return typeof m === "object" && "tag" in m && typeof m.tag === "number";
  }

  /**
   * Identify the name of an incoming message, if possible.
   */
  public nameForTag (tag : number) : string
  {
    const msgClass : ValidatingClass = INCOMING_MESSAGE_MAP.get(tag);
    if (msgClass)
    {
      return `${msgClass.name}`;
    }
    return `Unknown [tag: ${tag}]`;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/debug/mock-receiver-modal.component.ts